/**
 * <QuizChoiceRow> — une réponse d'une question à choix, qu'on touche (molecule).
 *
 * La ligne du « Défi éclair » de l'attente de correction : sa lettre dans une pastille, le texte,
 * et, une fois la question répondue, ce qu'elle était. Quatre états, en props :
 *  - `idle` : la réponse se touche ; la lettre sur l'aplat de l'univers ;
 *  - `right` : la bonne réponse, sur la tinte de progression, une coche ;
 *  - `wrong` : la réponse choisie qui n'était pas la bonne, sur la tinte d'erreur, une croix ;
 *  - `inert` : une autre réponse, après coup : elle ne se touche plus ;
 *  - `selected` : la réponse choisie, pas encore envoyée (le quiz de prérequis, qui ne la corrige
 *    qu'à la fin) : sur l'aplat de l'univers ; elle se touche encore, une autre aussi.
 * Ce n'est pas `<OptionCard>` : une option se choisit puis se valide, ici le premier appui
 * répond.
 *
 * `size="sm"` : la ligne dense d'une relecture qu'on parcourt (le quiz relu au centre du live),
 * une réponse par ligne, comme l'ancienne fiche ; le défi garde sa cible au doigt (`md`, défaut).
 */
import React from "react"
import { Pressable, View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { Icon } from "../../atoms/icons/Icon"
import { Text } from "../../atoms/typography/Text"
import { RichText } from "../RichText"
import { type SemanticColorKey } from "../../theme/tokens/colors"

const LETTERS = ["A", "B", "C", "D", "E", "F", "G", "H"]

/** La lettre du choix à cette place : A à H, puis « ? ». */
export function quizChoiceLetter(index: number): string {
    return LETTERS[index] ?? "?"
}

export type QuizChoiceState = "idle" | "right" | "wrong" | "inert" | "selected"
export type QuizChoiceSize = "md" | "sm"

export type QuizChoiceRowProps = {
    /** « A », « B »… */
    letter: string
    label: string
    /**
     * `rich` : la réponse porte une formule, elle se rend comme l'énoncé (`<RichText>`, une WebView
     * sur téléphone : seulement pour une réponse à formule). Défaut `plain`.
     */
    labelFormat?: "plain" | "rich"
    /** Défaut `idle`. */
    state?: QuizChoiceState
    /** `md` (défaut) : la cible du défi ; `sm` : la ligne dense d'une relecture. */
    size?: QuizChoiceSize
    /** La réponse touchée. Seulement en `idle` et `selected`. */
    onPress?: () => void
    testID?: string
}

/** La surface, la pastille, son encre et celle du texte, par état. */
const LOOK: Record<
    QuizChoiceState,
    { surface: SemanticColorKey; disc: SemanticColorKey; discInk: SemanticColorKey; ink: SemanticColorKey }
> = {
    idle: { surface: "surface-raised", disc: "role-fill", discInk: "text-on-role", ink: "text-primary" },
    right: { surface: "successLight", disc: "success", discInk: "white", ink: "success-strong" },
    wrong: { surface: "dangerLight", disc: "danger-fill", discInk: "white", ink: "danger-strong" },
    inert: { surface: "surface-raised", disc: "surface-subtle", discInk: "text-muted", ink: "text-muted" },
    selected: { surface: "role-selected", disc: "text-on-role", discInk: "role-selected", ink: "text-on-role" },
}

/** La pastille, sa lettre, le texte et la marque, par taille. */
const METRICS = {
    md: { disc: 28, letter: "text-md", label: "text-base", mark: 20 },
    sm: { disc: 20, letter: "text-xs", label: "text-md", mark: 16 },
} as const

export function QuizChoiceRow(props: QuizChoiceRowProps) {
    const { letter, label, labelFormat = "plain", state = "idle", size = "md", onPress, testID } = props
    const look = LOOK[state]
    const metrics = METRICS[size]
    const pressable = (state === "idle" || state === "selected") && onPress != null

    return (
        <Pressable
            onPress={pressable ? onPress : undefined}
            disabled={!pressable}
            accessibilityRole="button"
            accessibilityState={{
                disabled: !pressable,
                selected: state === "right" || state === "wrong" || state === "selected",
            }}
            style={({ pressed }) => [styles.row(look.surface, size), pressed && styles.pressed]}
            testID={testID}
        >
            <View style={styles.disc(look.disc, metrics.disc)}>
                <Text size={metrics.letter} weight="bold" color={look.discInk}>
                    {letter}
                </Text>
            </View>
            {labelFormat === "rich" ? (
                // La WebView de la formule (téléphone) ne prend pas le toucher : la rangée choisit la réponse.
                <View style={styles.label} pointerEvents="none">
                    <RichText value={label} color={look.ink} align="start" compact={size === "sm"} />
                </View>
            ) : (
                <Text size={metrics.label} weight="semibold" color={look.ink} style={styles.label}>
                    {label}
                </Text>
            )}
            {state === "right" && <Icon name="check" size={metrics.mark} color={look.ink} />}
            {state === "wrong" && <Icon name="times" size={metrics.mark} color={look.ink} />}
            {state === "selected" && <Icon name="check" size={metrics.mark} color={look.ink} />}
        </Pressable>
    )
}

const styles = StyleSheet.create((theme) => ({
    row: (surface: SemanticColorKey, size: QuizChoiceSize) => ({
        flexDirection: "row" as const,
        alignItems: "center" as const,
        gap: theme.spacing[size === "sm" ? "2" : "3"],
        minHeight: size === "sm" ? 32 : 48,
        paddingVertical: theme.spacing[size === "sm" ? "1" : "2"],
        paddingHorizontal: theme.spacing[size === "sm" ? "2" : "3"],
        borderRadius: theme.radius.md,
        backgroundColor: theme.colors[surface],
    }),
    pressed: { opacity: 0.85 },
    disc: (color: SemanticColorKey, diameter: number) => ({
        width: diameter,
        height: diameter,
        borderRadius: diameter / 2,
        alignItems: "center" as const,
        justifyContent: "center" as const,
        backgroundColor: theme.colors[color],
    }),
    label: { flex: 1, flexShrink: 1 },
}))
